import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Cloud as CloudIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getTrail, updateTrail } from '../../../services/portalApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading,
  PortalErrorState,
  PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import {
  CK,
  FONT_BODY,
  sectionCardSx,
  insetPanelSx,
  emptyTextSx,
  splitRowSx,
  actionRowSx, tileEntranceStyle
} from '../portalStyles';

const dataResourceTypes = [
  'AWS::S3::Object',
  'AWS::Lambda::Function',
  'AWS::DynamoDB::Table',
];

const networkEventSources = [
  'aco-automation.amazonaws.com',
  'appconfig.amazonaws.com',
  'application-signals.amazonaws.com',
  'appmesh.amazonaws.com',
  'athena.amazonaws.com',
  'b2bi.amazonaws.com',
  'backup-gateway.amazonaws.com',
  'bcm-data-exports.amazonaws.com',
  'bcm-pricing-calculator.amazonaws.com',
  'bedrock.amazonaws.com',
  'billing.amazonaws.com',
  'cassandra.amazonaws.com',
  'ce.amazonaws.com',
  'cloudcontrolapi.amazonaws.com',
  'cloudformation.amazonaws.com',
  'cloudhsm.amazonaws.com',
  'cloudtrail.amazonaws.com',
  'codedeploy.amazonaws.com',
  'comprehend.amazonaws.com',
  'comprehendmedical.amazonaws.com',
  'config.amazonaws.com',
  'ds.amazonaws.com',
  'dynamodb.amazonaws.com',
  'ec2.amazonaws.com',
  'ecs.amazonaws.com',
  'elasticfilesystem.amazonaws.com',
  'elasticloadbalancing.amazonaws.com',
  'events.amazonaws.com',
  'firehose.amazonaws.com',
  'frauddetector.amazonaws.com',
  'freetier.amazonaws.com',
  'fsx.amazonaws.com',
  'glue.amazonaws.com',
  'healthlake.amazonaws.com',
  'invoicing.amazonaws.com',
  'iot.amazonaws.com',
  'iotfleetwise.amazonaws.com',
  'iotsecuredtunneling.amazonaws.com',
  'kms.amazonaws.com',
  'lakeformation.amazonaws.com',
  'lambda.amazonaws.com',
  'license-manager.amazonaws.com',
  'lookoutequipment.amazonaws.com',
  'lookoutvision.amazonaws.com',
  'monitoring.amazonaws.com',
  'personalize.amazonaws.com',
  'qbusiness.amazonaws.com',
  'rds.amazonaws.com',
  'rekognition.amazonaws.com',
  'rolesanywhere.amazonaws.com',
  's3.amazonaws.com',
  'sagemaker.amazonaws.com',
  'scheduler.amazonaws.com',
  'secretsmanager.amazonaws.com',
  'servicediscovery.amazonaws.com',
  'sns.amazonaws.com',
  'sqs.amazonaws.com',
  'ssm-contacts.amazonaws.com',
  'ssm.amazonaws.com',
  'storagegateway.amazonaws.com',
  'swf.amazonaws.com',
  'textract.amazonaws.com',
  'transcribe.amazonaws.com',
  'transcribestreaming.amazonaws.com',
  'transform.amazonaws.com',
  'translate.amazonaws.com',
  'user-subscriptions.amazonaws.com',
  'verifiedpermissions.amazonaws.com',
  'voiceid.amazonaws.com',
  'workmail.amazonaws.com',
  'workmailmessageflow.amazonaws.com',
];

// Heading for the inset panels inside the Event Configuration card.
const panelHeadingSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  fontSize: '0.95rem',
  color: CK.navy,
};

// Read-only label/value tile, matching the trail detail metadata treatment.
const MetaTile = ({ label, value }) => (
  <Box sx={{ ...insetPanelSx, p: 2, height: '100%' }}>
    <Typography
      sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', fontWeight: 500, color: CK.muted, mb: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 600, color: CK.navy, wordBreak: 'break-word' }}
    >
      {value}
    </Typography>
  </Box>
);

const EditTrail = () => {
  const { trailId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [trail, setTrail] = useState(null);

  // Trail Settings
  const [isMultiRegion, setIsMultiRegion] = useState(true);
  const [s3Prefix, setS3Prefix] = useState('');

  // Event Configuration
  const [mgmtEnabled, setMgmtEnabled] = useState(true);
  const [mgmtReadWriteType, setMgmtReadWriteType] = useState('All');
  const [excludeKMS, setExcludeKMS] = useState(false);
  const [excludeRDSData, setExcludeRDSData] = useState(false);
  const [dataEvents, setDataEvents] = useState([]);
  const [insightApiCall, setInsightApiCall] = useState(false);
  const [insightApiError, setInsightApiError] = useState(false);
  const [networkEvents, setNetworkEvents] = useState([]);

  const fetchTrail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTrail(trailId);
      const data = response.data;
      setTrail(data);

      // Populate form state
      setIsMultiRegion(data.isMultiRegion);
      setS3Prefix(data.s3Prefix || '');

      // Parse event config
      const ec = data.eventConfig;
      if (ec) {
        if (ec.managementEvents) {
          setMgmtEnabled(ec.managementEvents.enabled);
          setMgmtReadWriteType(ec.managementEvents.readWriteType || 'All');
          setExcludeKMS(ec.managementEvents.excludeKMS || false);
          setExcludeRDSData(ec.managementEvents.excludeRDSData || false);
        } else {
          setMgmtEnabled(false);
        }

        if (ec.dataEvents && ec.dataEvents.length > 0) {
          setDataEvents(
            ec.dataEvents.map((de) => ({
              resourceType: de.resourceType,
              readWriteType: de.readWriteType || 'All',
              resourceArns: (de.resourceArns || []).join(', '),
            }))
          );
        }

        if (ec.insightSelectors && ec.insightSelectors.length > 0) {
          ec.insightSelectors.forEach((is) => {
            if (is.insightType === 'ApiCallRateInsight') setInsightApiCall(true);
            if (is.insightType === 'ApiErrorRateInsight') setInsightApiError(true);
          });
        }

        if (ec.networkActivityEvents && ec.networkActivityEvents.length > 0) {
          const legacyMap = {
            'AWS::EC2::VPC': 'ec2.amazonaws.com',
            'AWS::EC2::Subnet': 'ec2.amazonaws.com',
            'AWS::EC2::NetworkInterface': 'ec2.amazonaws.com',
          };
          setNetworkEvents(
            ec.networkActivityEvents.map((ne) => ({
              eventSource: ne.eventSource || legacyMap[ne.resourceType] || '',
            }))
          );
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [trailId]);

  useEffect(() => {
    fetchTrail();
  }, [fetchTrail]);

  const addDataEvent = () => {
    setDataEvents([...dataEvents, { resourceType: 'AWS::S3::Object', readWriteType: 'All', resourceArns: '' }]);
  };

  const removeDataEvent = (index) => {
    setDataEvents(dataEvents.filter((_, i) => i !== index));
  };

  const updateDataEvent = (index, field, value) => {
    const updated = [...dataEvents];
    updated[index] = { ...updated[index], [field]: value };
    setDataEvents(updated);
  };

  const addNetworkEvent = () => {
    setNetworkEvents([...networkEvents, { eventSource: '' }]);
  };

  const removeNetworkEvent = (index) => {
    setNetworkEvents(networkEvents.filter((_, i) => i !== index));
  };

  const updateNetworkEvent = (index, field, value) => {
    const updated = [...networkEvents];
    updated[index] = { ...updated[index], [field]: value };
    setNetworkEvents(updated);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const eventConfig = {};

    if (mgmtEnabled) {
      eventConfig.managementEvents = {
        enabled: true,
        readWriteType: mgmtReadWriteType,
        excludeKMS,
        excludeRDSData,
      };
    }

    if (dataEvents.length > 0) {
      eventConfig.dataEvents = dataEvents.map((de) => ({
        resourceType: de.resourceType,
        readWriteType: de.readWriteType,
        resourceArns: de.resourceArns
          ? de.resourceArns.split(',').map((s) => s.trim()).filter(Boolean).map((arn) =>
              de.resourceType === 'AWS::S3::Object' && !arn.endsWith('/') ? arn + '/' : arn
            )
          : [],
      }));
    }

    const insightSelectors = [];
    if (insightApiCall) insightSelectors.push({ insightType: 'ApiCallRateInsight' });
    if (insightApiError) insightSelectors.push({ insightType: 'ApiErrorRateInsight' });
    if (insightSelectors.length > 0) {
      eventConfig.insightSelectors = insightSelectors;
    }

    if (networkEvents.length > 0) {
      eventConfig.networkActivityEvents = networkEvents.map((ne) => ({
        eventSource: ne.eventSource,
      }));
    }

    try {
      await updateTrail(trailId, {
        isMultiRegion,
        s3Prefix,
        eventConfig,
      });

      toast.success('Trail update started — tracking progress');
      navigate(`/cloudtrail/trails/${trailId}`);
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to update trail: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(`/cloudtrail/trails/${trailId}`)}
      sx={{ color: CK.body, fontFamily: FONT_BODY, fontWeight: 600, px: 1.5, mb: 2 }}
    >
      Back to Trail
    </Button>
  );

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          {backButton}
          <PortalPageHeader
            icon={<CloudIcon />}
            title="Edit Trail"
            subtitle="Loading trail details"
            color="#4698D3"
          />
        </motion.div>

        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading trail…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error && !trail) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          {backButton}
          <PortalPageHeader
            icon={<CloudIcon />}
            title="Edit Trail"
            subtitle="This trail could not be loaded"
            color="#4698D3"
          />
        </motion.div>

        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={fetchTrail} />
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        {backButton}
        <PortalPageHeader
          icon={<CloudIcon />}
          title="Edit Trail"
          subtitle={trail.name}
          color="#4698D3"
        />
      </motion.div>

      {error && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </motion.div>
      )}

      {/* Trail Settings */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <PortalSectionHeader
            icon={<CloudIcon sx={{ fontSize: 20 }} />}
            title="Trail settings"
            subtitle="Name and region are fixed once a trail is created"
            color={CK.blue}
          />

          {/* No motion wrapper: the tiles animate themselves in pure CSS, and a
              motion component here can write an inline opacity onto them once
              its own animation settles. */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <div style={tileEntranceStyle(0)}>
                  <MetaTile label="Trail Name" value={trail.name} />
                </div>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <div style={tileEntranceStyle(1)}>
                  <MetaTile label="Region" value={trail.region || 'us-east-1'} />
                </div>
              </Grid>
            </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={isMultiRegion}
                onChange={(e) => setIsMultiRegion(e.target.checked)}
                color="primary"
              />
            }
            label="Multi-region trail"
          />
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted, ml: 6, mt: -0.5, mb: 3 }}>
            Log events from all AWS regions
          </Typography>

          <TextField
            label="S3 Key Prefix (optional)"
            value={s3Prefix}
            onChange={(e) => setS3Prefix(e.target.value)}
            fullWidth
            helperText="Optional prefix for CloudTrail log files in the bucket"
          />
        </Paper>
      </motion.div>

      {/* Event Configuration */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<EventNoteIcon sx={{ fontSize: 20 }} />}
            title="Event configuration"
            subtitle="Choose which activity this trail records"
            color={CK.blue}
          />

          {/* Management Events */}
          <Paper elevation={0} sx={{ ...insetPanelSx, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch checked={mgmtEnabled} onChange={(e) => setMgmtEnabled(e.target.checked)} />
              }
              label={<Typography sx={panelHeadingSx}>Management Events</Typography>}
            />
            {mgmtEnabled && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 200, mb: 1 }}>
                  <InputLabel>Read/Write Type</InputLabel>
                  <Select
                    value={mgmtReadWriteType}
                    onChange={(e) => setMgmtReadWriteType(e.target.value)}
                    label="Read/Write Type"
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="ReadOnly">Read Only</MenuItem>
                    <MenuItem value="WriteOnly">Write Only</MenuItem>
                  </Select>
                </FormControl>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={excludeKMS} onChange={(e) => setExcludeKMS(e.target.checked)} size="small" />}
                    label="Exclude AWS KMS events"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={excludeRDSData} onChange={(e) => setExcludeRDSData(e.target.checked)} size="small" />}
                    label="Exclude Amazon RDS Data API events"
                  />
                </FormGroup>
              </Box>
            )}
          </Paper>

          {/* Data Events */}
          <Paper elevation={0} sx={{ ...insetPanelSx, mb: 3 }}>
            <Box sx={{ ...splitRowSx, mb: 1 }}>
              <Typography sx={panelHeadingSx}>Data Events</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addDataEvent} sx={{ px: 2 }}>
                Add
              </Button>
            </Box>
            {dataEvents.length === 0 && (
              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem' }}>
                No data event selectors yet — add one to log object-level activity.
              </Typography>
            )}
            {dataEvents.map((de, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap' }}>
                <Autocomplete
                  options={dataResourceTypes}
                  value={de.resourceType}
                  onChange={(_, newValue) => updateDataEvent(idx, 'resourceType', newValue || 'AWS::S3::Object')}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 260 } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Resource Type" placeholder="Search resource types..." />
                  )}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Read/Write</InputLabel>
                  <Select
                    value={de.readWriteType}
                    onChange={(e) => updateDataEvent(idx, 'readWriteType', e.target.value)}
                    label="Read/Write"
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="ReadOnly">Read Only</MenuItem>
                    <MenuItem value="WriteOnly">Write Only</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Resource ARNs (comma-separated, optional)"
                  value={de.resourceArns}
                  onChange={(e) => updateDataEvent(idx, 'resourceArns', e.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeDataEvent(idx)}
                  sx={{
                    mt: 0.5,
                    color: CK.red,
                    borderRadius: '6px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { backgroundColor: '#FDF2F2' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Paper>

          {/* Insights */}
          <Paper elevation={0} sx={{ ...insetPanelSx, mb: 3 }}>
            <Typography sx={{ ...panelHeadingSx, mb: 1 }}>Insight Events</Typography>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={insightApiCall} onChange={(e) => setInsightApiCall(e.target.checked)} size="small" />}
                label="API call rate insight"
              />
              <FormControlLabel
                control={<Checkbox checked={insightApiError} onChange={(e) => setInsightApiError(e.target.checked)} size="small" />}
                label="API error rate insight"
              />
            </FormGroup>
          </Paper>

          {/* Network Activity */}
          <Paper elevation={0} sx={insetPanelSx}>
            <Box sx={{ ...splitRowSx, mb: 1 }}>
              <Typography sx={panelHeadingSx}>Network Activity Events</Typography>
              <Box sx={actionRowSx}>
                <Button
                  size="small"
                  sx={{ px: 2 }}
                  onClick={() => {
                    const existing = new Set(networkEvents.map((ne) => ne.eventSource));
                    const toAdd = networkEventSources.filter((src) => !existing.has(src));
                    if (toAdd.length > 0) {
                      setNetworkEvents([...networkEvents, ...toAdd.map((src) => ({ eventSource: src }))]);
                    }
                  }}
                >
                  Select All
                </Button>
                <Button size="small" startIcon={<AddIcon />} onClick={addNetworkEvent} sx={{ px: 2 }}>
                  Add
                </Button>
              </Box>
            </Box>
            {networkEvents.length === 0 && (
              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem' }}>
                No network activity selectors yet — add one to log VPC endpoint access.
              </Typography>
            )}
            {networkEvents.map((ne, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                <Autocomplete
                  freeSolo
                  options={networkEventSources}
                  value={ne.eventSource || ''}
                  onChange={(_, newValue) => updateNetworkEvent(idx, 'eventSource', newValue || '')}
                  onInputChange={(_, newInput, reason) => {
                    if (reason === 'input') updateNetworkEvent(idx, 'eventSource', newInput);
                  }}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 300 }, flex: 1 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Event Source" placeholder="e.g. ec2.amazonaws.com" />
                  )}
                />
                <IconButton
                  size="small"
                  onClick={() => removeNetworkEvent(idx)}
                  sx={{
                    color: CK.red,
                    borderRadius: '6px',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { backgroundColor: '#FDF2F2' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Paper>

          {/* Save Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              mt: 4,
              pt: 2,
              borderTop: `1px solid ${CK.border}`,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(`/cloudtrail/trails/${trailId}`)}
              sx={{ px: 3, color: CK.body, borderColor: CK.border }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ px: 4, '&.Mui-disabled': { backgroundColor: CK.border, color: CK.muted } }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </motion.div>
  );
};

export default EditTrail;
